import { useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Box,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Modal,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { FormikProps } from 'formik';

import { CompanyLookUpList } from 'src/components';
import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import { CounterInput } from 'src/components/CounterInput';
import { CustomInput } from 'src/components/CustomInput';
import { InputWithSearch } from 'src/components/InputWithSearch';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import {
    DOWNLOAD_B1_FORM,
    DOWNLOAD_PARTNERSHIP_DEED_FILE,
} from 'src/graphql/mutations/downloadB1Form.mutation';
import { REGENERATE_PRESIGNED_URL } from 'src/graphql/mutations/generateAwsURL.mutation';
import { COMPANY_LOOKUP } from 'src/graphql/queries/companyLookup';
import { useValidateVAT } from 'src/hooks';
import { isIreland } from 'src/lib/countryConfig';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { MerchantInformation_FormValues } from 'src/types/AddMerchant/merchantInfo';
import { extractAddress } from 'src/utils/utils';
import { CompanyLookupResponse, CompanyReportResponse } from 'types/graphql';

import { principalInitialValue } from '../../steps/MerchantInformation/components/Principal';

enum BusinessType {
    PRIVATE_LIMITED_COMPANY = 'Private Limited Company – LTD',
    PUBLIC_LIMITED_COMPANY = 'Public Limited Company – PLC',
    LIMITED_LIABILITY_PARTNERSHIP = 'Limited Liability Partnership – LLP',
    PARTNERSHIP = 'Partnership',
    SOLE_TRADER = 'Sole Trader',
}

const BUSINESS_TYPES = [
    { key: 'PRIVATE_LIMITED_COMPANY', value: 'Private Limited Company - LTD' },
    { key: 'PUBLIC_LIMITED_COMPANY', value: 'Public Limited Company – PLC' },
    {
        key: 'LIMITED_LIABILITY_PARTNERSHIP',
        value: 'Limited Liability Partnership – LLP',
    },
    { key: 'PARTNERSHIP', value: 'Partnership' },
    { key: 'SOLE_TRADER', value: 'Sole Trader' },
];

type FileDownloadModalProps = {
    url: string | undefined;
    title: string | undefined;
    fileName: string | undefined;
};

export const AboutBusinessForm = ({
    values,
    setFieldValue,
    setFieldError,
    errors,
}: FormikProps<MerchantInformation_FormValues>) => {
    // Modal state management
    const [isCompaniesOpen, setIsCompaniesOpen] = useState(false);
    const [isFileDownloadModalOpen, setIsFileDownloadModalOpen] = useState(false);
    const { validatingVatNumber } = useValidateVAT();
    const { merchantApplication } = useAddMerchantContext();
    const [regenerateUrl] = useMutation(REGENERATE_PRESIGNED_URL);

    const [fileDownloadProps, setFileDownloadProps] = useState<FileDownloadModalProps>({
        title: undefined,
        url: undefined,
        fileName: undefined,
    });

    const isPartnershipBusinessType = () => {
        return values?.aboutBusiness?.businessType?.trim()?.toUpperCase() === 'PARTNERSHIP';
    };

    // Company data
    const [companies, setCompanies] = useState<CompanyLookupResponse[]>([]);
    const [companyLookUp, { loading: searchingForCompany }] = useLazyQuery(COMPANY_LOOKUP, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: (data) => {
            setCompanies(data.companyLookUp);
        },
    });

    const [downloadB1Form] = useMutation(DOWNLOAD_B1_FORM, {
        onError: (error) => {
            return showErrorToast(`Failed to download B1 Form: ${error.message}`);
        },
        onCompleted: async (data) => {
            if (data.downloadB1Form?.url) {
                const regeneratedUrl = await regenerateUrl({
                    variables: { url: data.downloadB1Form?.url },
                });

                setFieldValue('b1FormUrl', regeneratedUrl.data?.regenerateSecureAWSUrl);
                setFileDownloadProps(() => {
                    return {
                        url: regeneratedUrl.data?.regenerateSecureAWSUrl,
                        title: 'B1 Form',
                        fileName: 'B1 Form.pdf',
                    };
                });
                setIsFileDownloadModalOpen(true);
            }
        },
    });

    const [downloadPartnershipDeedFile] = useMutation(DOWNLOAD_PARTNERSHIP_DEED_FILE, {
        onError: (error) => {
            return showErrorToast(`Failed to download Partnership Deed: ${error.message}`);
        },
        onCompleted: async (data) => {
            if (data.downloadPartnershipDeedFile?.url) {
                const regeneratedUrl = await regenerateUrl({
                    variables: { url: data.downloadPartnershipDeedFile?.url },
                });
                setFieldValue('partnershipDeedUrl', regeneratedUrl.data?.regenerateSecureAWSUrl);
                setFileDownloadProps(() => {
                    return {
                        url: regeneratedUrl.data?.regenerateSecureAWSUrl,
                        title: 'Partnership Deed',
                        fileName: 'Partnership Deed.pdf',
                    };
                });
                setIsFileDownloadModalOpen(true);
            }
        },
    });

    const handleInputReset = (value, target) => {
        if (!value) {
            setFieldValue(target, '');
        }
    };

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    const showCompanySearch =
        values.aboutBusiness.businessType !== 'SOLE_TRADER' &&
        (values.aboutBusiness.businessType !== 'PARTNERSHIP' || isIreland);

    return (
        <MainCard>
            {(searchingForCompany || validatingVatNumber) && <Loader />}
            <Typography fontSize="24px" color="#1D1D1B" marginBottom="8px">
                About Business
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />

            <Box className="mt-20">
                <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                    {/* Row 1 */}
                    <Box>
                        <Grid container spacing={3} display="flex" alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <SelectBaseField
                                    label="Business Type"
                                    name="aboutBusiness.businessType"
                                    inputLabel="Please Select Business Type"
                                    menuItems={BUSINESS_TYPES}
                                    initialValue={values.aboutBusiness.businessType}
                                    disabled={isEditingDisabled}
                                />
                            </Grid>
                            {/* Charity section */}
                            {values.aboutBusiness.businessType !==
                                BusinessType.PUBLIC_LIMITED_COMPANY && (
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap="16px">
                                            <FormLabel id="is-charity" sx={{ color: '#575757' }}>
                                                Charity or Non-profit?
                                            </FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="is-charity-radio-buttons"
                                                    defaultValue={
                                                        values.aboutBusiness.isCharity || false
                                                    }
                                                    name="isCharity"
                                                    row
                                                    onChange={(event) => {
                                                        const value = JSON.parse(
                                                            event.target.value
                                                        );
                                                        setFieldValue(
                                                            'aboutBusiness.isCharity',
                                                            value
                                                        );
                                                        handleInputReset(
                                                            value,
                                                            'aboutBusiness.charityNumber'
                                                        );
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value
                                                        control={<Radio />}
                                                        label="Yes"
                                                        disabled={isEditingDisabled}
                                                    />
                                                    <FormControlLabel
                                                        value={false}
                                                        control={<Radio />}
                                                        label="No"
                                                        disabled={isEditingDisabled}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                        {values.aboutBusiness.isCharity && (
                                            <InputBaseField
                                                label="Charity Number"
                                                name="aboutBusiness.charityNumber"
                                                placeholder="Enter Charity Number"
                                                disabled={isEditingDisabled}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    {/* Row 2 */}
                    {/* Enable searching by company name only if its not a partnership, sole trader or charity */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                {/* If the business type is not a partnership or sole_trader then enable company lookup */}
                                {showCompanySearch ? (
                                    <InputWithSearch
                                        searchText="Search"
                                        onSearch={async (value) => {
                                            await companyLookUp({
                                                variables: { companyName: value.toLowerCase() },
                                            });
                                            setIsCompaniesOpen(true);
                                        }}
                                        placeholder="Enter Legal/Company Name"
                                        label="Legal Company Name"
                                        fieldName="aboutBusiness.legalCompanyName"
                                        disabled={isEditingDisabled || searchingForCompany}
                                    />
                                ) : (
                                    <InputBaseField
                                        label="Legal Company Name"
                                        name="aboutBusiness.legalCompanyName"
                                        placeholder="Enter Legal/Company Name"
                                        disabled={isEditingDisabled || searchingForCompany}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Website"
                                    name="aboutBusiness.website"
                                    placeholder="Website Address"
                                    disabled={isEditingDisabled}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Row 3 */}
                    {showCompanySearch && (
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputWithSearch
                                        searchText="Search"
                                        onSearch={async (value) => {
                                            await companyLookUp({
                                                variables: {
                                                    companyNumber: value.toString(),
                                                },
                                            });
                                            setIsCompaniesOpen(true);
                                        }}
                                        placeholder="Enter Registered Number"
                                        label="Registered Number"
                                        fieldName="aboutBusiness.registeredNumber"
                                        disabled={isEditingDisabled || searchingForCompany}
                                    />
                                </Grid>
                                {isIreland && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <InputWithSearch
                                                searchText="View"
                                                onSearch={async (value) => {
                                                    if (value) {
                                                        await downloadB1Form({
                                                            variables: {
                                                                id: Number.parseInt(
                                                                    value.toString(),
                                                                    10
                                                                ),
                                                            },
                                                        });
                                                    }
                                                }}
                                                placeholder="B1 Form"
                                                label="B1 Form"
                                                fieldName="b1FormId"
                                                disabled={isEditingDisabled || searchingForCompany}
                                            />
                                        </Grid>

                                        {isPartnershipBusinessType() && (
                                            <Grid item xs={12} md={6}>
                                                <InputWithSearch
                                                    searchText="View"
                                                    onSearch={async (value) => {
                                                        if (value) {
                                                            await downloadPartnershipDeedFile({
                                                                variables: {
                                                                    id: Number.parseInt(
                                                                        value.toString(),
                                                                        10
                                                                    ),
                                                                },
                                                            });
                                                        }
                                                    }}
                                                    placeholder="Partnership Deed"
                                                    label="Partnership Deed"
                                                    fieldName="partnershipDeedId"
                                                    disabled={
                                                        isEditingDisabled || searchingForCompany
                                                    }
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </Box>

            <Divider
                sx={{
                    borderColor: '#DADADA',
                }}
            />
            <Box className="mt-20">
                <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                    {/* Row 4 */}
                    <Box>
                        <Grid container spacing={3} display="flex" alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <InputBaseField
                                    label="Trading Name"
                                    name="aboutBusiness.tradingName"
                                    placeholder="Enter Trading Name"
                                    disabled={isEditingDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Box display="flex" alignItems="center" gap="16px">
                                        <FormLabel id="world-pay-seller" sx={{ color: '#575757' }}>
                                            VAT Registered
                                        </FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                aria-labelledby="vat-registered-radio-buttons"
                                                defaultValue={
                                                    values.aboutBusiness.vat.vatRegistered || false
                                                }
                                                name="aboutBusiness.vat.vatRegistered"
                                                row
                                                onChange={(event) => {
                                                    const value = JSON.parse(event.target.value);
                                                    setFieldValue(
                                                        'aboutBusiness.vat.vatRegistered',
                                                        value
                                                    );
                                                    handleInputReset(
                                                        value,
                                                        'aboutBusiness.vat.vatNumber'
                                                    );
                                                }}
                                            >
                                                <FormControlLabel
                                                    value
                                                    control={<Radio />}
                                                    label="Yes"
                                                    disabled={isEditingDisabled}
                                                />
                                                <FormControlLabel
                                                    value={false}
                                                    control={<Radio />}
                                                    label="No"
                                                    disabled={isEditingDisabled}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                    {values.aboutBusiness.vat.vatRegistered && (
                                        <CustomInput
                                            name="aboutBusiness.vat.vatNumber"
                                            label="VAT Number"
                                            subLabel="(Please include the FULL VAT Number inc the country code)"
                                            value={values.aboutBusiness.vat.vatNumber}
                                            placeholder="Enter VAT Number"
                                            onChange={(e) => {
                                                setFieldValue(
                                                    'aboutBusiness.vat.vatNumber',
                                                    e.target.value
                                                );
                                            }}
                                            error={errors.aboutBusiness?.vat?.vatNumber}
                                            disabled={isEditingDisabled}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 5 */}
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <CounterInput
                                    name="numberOfOutlets"
                                    label="Number of Outlets"
                                    setFieldValue={setFieldValue}
                                    disabled={isEditingDisabled}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </Box>

            {/* company Search Modal */}
            <Modal
                open={isCompaniesOpen}
                onClose={() => {
                    return setIsCompaniesOpen(false);
                }}
            >
                <CompanyLookUpList
                    data={companies}
                    onClose={() => {
                        return setIsCompaniesOpen(false);
                    }}
                    setFieldValue={setFieldValue}
                    onSelectCompanyReport={(companyReport: CompanyReportResponse) => {
                        const { addressLine1, town, county } = extractAddress(
                            companyReport?.companyAddress
                        );

                        companyReport?.companyName &&
                            setFieldValue(
                                'aboutBusiness.legalCompanyName',
                                companyReport?.companyName
                            );

                        companyReport?.websiteURL &&
                            setFieldValue('aboutBusiness.website', companyReport?.websiteURL);

                        companyReport?.registrationNumber &&
                            setFieldValue(
                                'aboutBusiness.registeredNumber',
                                companyReport?.registrationNumber
                            );

                        companyReport.eircode &&
                            setFieldValue(
                                'aboutBusiness.registeredAddress.eircode',
                                companyReport?.eircode
                            );

                        addressLine1 &&
                            setFieldValue(
                                'aboutBusiness.registeredAddress.addressLine1',
                                addressLine1
                            );

                        town && setFieldValue('aboutBusiness.registeredAddress.townOrCity', town);

                        county && setFieldValue('aboutBusiness.registeredAddress.county', county);

                        if (companyReport?.directors?.length > 0) {
                            const totalShares = companyReport?.shareholderReport?.totalShares ?? 0;
                            const principals = companyReport.directors.map((director, index) => {
                                const numberOfShares = director?.numberOfShares ?? 0;
                                return {
                                    ...principalInitialValue,
                                    ...director,
                                    isBillingContact: index === 0,
                                    isBeneficialOwner: numberOfShares > 0,
                                    percentageOfOwnership: (numberOfShares / totalShares) * 100,
                                };
                            });

                            setFieldValue('principals', principals);
                        }
                        setIsCompaniesOpen(false);
                    }}
                />
            </Modal>

            <ViewPDFModal
                file={null}
                isModalOpen={isFileDownloadModalOpen}
                setIsModalOpen={setIsFileDownloadModalOpen}
                title={fileDownloadProps.title}
                fileURL={fileDownloadProps.url}
                fileName={`${fileDownloadProps.fileName}.pdf`}
            />
        </MainCard>
    );
};
